/* eslint-disable @typescript-eslint/explicit-function-return-type */
import { Icons } from '@everlywell/leaves';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { Experiment, Variant } from 'components/Experiment';
import StripePayment from 'components/Stripe/StripePayment';
import React, { FormEvent, useState, useEffect, useRef } from 'react';
import { ANALYTICS } from 'utils/constants/analytics';
import { formatPrice } from 'utils/helpers';
import { stripePublicKey } from 'utils/helpers/paymentHelpers';
import { LineItem, FormFieldType, CartStateType } from 'utils/types';

import emptyCartImage from '../../images/empty-cart.svg';
import { currentOrderGrandTotalInCents } from '../../utils/cartHelpers';
import CartProduct from './CartProduct';
import CartUpsell from './CartUpsell';
import { Chip } from './Chip';
import ErrorText from './ErrorText';
import Input from './Input';
import * as S from './styles';

declare global {
  interface Window {
    appRoot: string;
  }
}

// Make sure to call `loadStripe` outside of a component’s render to avoid
// recreating the `Stripe` object on every render.
// const stripePromise = loadStripe(stripePublicKey);

type Props = {
  cartState: CartStateType;
  removeProduct: Function;
  changeProductQuantity: Function;
  openFloatCart: Function;
  closeFloatCart: Function;
  handlePromoLinkChange: Function;
  handleRemovePromo: Function;
  handlePromoApply: (event: FormEvent<HTMLFormElement>) => void;
  handlePromoChange: (formField: FormFieldType) => void;
  submittingPromo: boolean;
  setTOS: Function;
  showLoading: boolean;
  upsellCard?: {
    textAccent: string;
    title: string;
    description: string;
    buttonText: string;
    upgradeCost: string;
    backgroundImageUrl: string;
    handleOnClick: () => void;
  };
  showAltPayments: boolean;
};

const Cart = (props: Props) => {
  const {
    cartState,
    removeProduct,
    changeProductQuantity,
    openFloatCart,
    closeFloatCart,
    handlePromoLinkChange,
    handlePromoApply,
    handlePromoChange,
    handleRemovePromo,
    setTOS,
    submittingPromo,
    upsellCard,
    showLoading,
    showAltPayments,
  } = props;

  if (!cartState) return null;

  const {
    currentOrder,
    lineItems,
    isOpen,
    cartTotal,
    cartErrors,
    isPromoApplied,
    promoInputValue,
    shouldShowPromoInput,
    promoErrors,
    currentPromoCode,
    showTOS,
  } = cartState;

  const products = lineItems.map((p: LineItem) => (
    <CartProduct
      product={p}
      removeProduct={removeProduct}
      changeProductQuantity={changeProductQuantity}
      key={p.variant_id}
    />
  ));
  const checkoutRef = useRef<HTMLAnchorElement>(null);
  const [stripePromise, setStripePromise] =
    useState<Promise<Stripe | null> | null>(null);
  const [hasLoaded, setHasLoaded] = useState(false);

  const hasOnlyLabVisit = lineItems.every((item) => item.is_lab_visit);

  useEffect(() => {
    setHasLoaded(true);
  }, [stripePromise]);

  useEffect(() => {
    if (isOpen && !stripePromise) setStripePromise(loadStripe(stripePublicKey));
    if (checkoutRef?.current) {
      isOpen ? checkoutRef.current.focus() : checkoutRef.current.blur();
    }
  }, [isOpen]);

  if (!hasLoaded) return null;

  return (
    <>
      <S.OpenCartBtn
        tabIndex={-1}
        data-testid="open-cart"
        aria-label="Open Cart"
        onClick={() => openFloatCart()}
      >
        {cartTotal ? (
          <S.CartInfoWrapper tabIndex={0}>
            <Icons.Cart />
            {products.length > 0 ? (
              <S.CartQuantity data-testid="cart-quantity">
                {cartTotal.productQuantity}
              </S.CartQuantity>
            ) : null}
          </S.CartInfoWrapper>
        ) : null}
      </S.OpenCartBtn>

      <S.CartContent
        data-testid="cart-content"
        id="cart-content"
        isOpen={isOpen}
        role="dialog"
        aria-expanded={isOpen}
        aria-live="polite"
      >
        <S.CartHeader>
          <S.CartCloseBtn
            tabIndex={isOpen ? 0 : -1}
            data-testid="close-cart"
            aria-label="Close Cart"
            onClick={() => closeFloatCart()}
          >
            <Icons.Close />
          </S.CartCloseBtn>
          <S.CartTitle aria-label="Your Cart">Your Cart</S.CartTitle>
        </S.CartHeader>

        <S.ShelfContainer>
          {!products.length && !showLoading ? (
            <S.EmptyCart data-testid="empty-cart">
              <S.EmptyCartImg
                alt="empty cart"
                src={emptyCartImage}
                width={114}
                height={80}
              />
              <p>Your shopping cart is empty</p>
            </S.EmptyCart>
          ) : (
            <>
              {cartErrors
                ? cartErrors.map((err) => <ErrorText>{err}</ErrorText>)
                : null}
              {products}

              {upsellCard ? (
                <Experiment id="fs-fsc-cart-upsell">
                  <Variant name="a">
                    <CartUpsell {...upsellCard} />
                  </Variant>
                </Experiment>
              ) : null}
              {!isPromoApplied ? (
                <S.PromoWrapper data-testid="cart-promo-wrapper">
                  <S.PromoApplyLink
                    onClick={() => handlePromoLinkChange()}
                    data-testid="cart-promo-link"
                  >
                    Have a promo code?
                    <S.PromoExpandIcon
                      shouldShowPromoInput={shouldShowPromoInput}
                    />
                  </S.PromoApplyLink>
                  <S.InputWrapper shouldShowPromoInput={shouldShowPromoInput}>
                    <form
                      onSubmit={handlePromoApply}
                      data-testid="cart-promo-form"
                    >
                      <Input
                        name="promo-input"
                        placeholder="Enter Promo Code"
                        onChange={handlePromoChange}
                        value={promoInputValue}
                        shouldFocus={shouldShowPromoInput}
                        onSubmit={() => {}}
                        data-testid="cart-promo-input"
                        submitting={submittingPromo}
                      />
                    </form>
                  </S.InputWrapper>
                </S.PromoWrapper>
              ) : null}
              {promoErrors
                ? promoErrors.map((err) => <ErrorText>{err}</ErrorText>)
                : null}
              <S.CartCalcWrapper>
                <S.LabelWrapper>
                  <S.PriceLabel>Subtotal:</S.PriceLabel>
                  {hasOnlyLabVisit ? null : (
                    <S.PriceLabel>Shipping:</S.PriceLabel>
                  )}
                  {isPromoApplied ? (
                    <S.AppliedPromoWrapper
                      code={currentPromoCode}
                      data-testid="cart-applied-promo-wrapper"
                    >
                      <S.PriceLabel>Applied:</S.PriceLabel>
                      {currentOrder.promotions &&
                      currentOrder.promotions.length > 0 ? (
                        <Chip
                          onRemove={handleRemovePromo}
                          data-testid="cart-applied-promo-code"
                        >
                          {currentOrder.promotions[0].code}
                        </Chip>
                      ) : null}
                    </S.AppliedPromoWrapper>
                  ) : null}
                </S.LabelWrapper>
                <S.ValueWrapper>
                  <S.Price data-testid="cart-subtotal">
                    ${formatPrice(cartTotal.subTotal)}
                  </S.Price>
                  {hasOnlyLabVisit ? null : <S.Price>FREE</S.Price>}
                  {isPromoApplied ? (
                    <S.DiscountAmount>
                      <span>
                        -$
                        {formatPrice(
                          parseFloat(currentOrder.promo_total.replace('-', '')),
                        )}
                      </span>
                    </S.DiscountAmount>
                  ) : null}
                </S.ValueWrapper>
              </S.CartCalcWrapper>
            </>
          )}
        </S.ShelfContainer>
        {showLoading ? (
          <S.LoadingScreen data-testid="cart-loading-screen" />
        ) : null}

        {cartTotal ? (
          <S.Footer>
            <S.TotalWrapper>
              <S.TotalLabel>
                Total ({cartTotal.productQuantity} Item
                {cartTotal.productQuantity > 1 ? 's' : ''}
                ):
              </S.TotalLabel>
              <S.TotalPrice data-testid="cart-total-price">
                {cartTotal.totalPrice
                  ? `$${formatPrice(cartTotal.totalPrice)}`
                  : '$0.00'}
              </S.TotalPrice>
            </S.TotalWrapper>
            <S.CheckoutLink
              role="button"
              aria-label="Checkout"
              ref={checkoutRef}
              //@ts-ignore
              href={
                typeof window !== 'undefined' &&
                // @ts-ignore
                typeof window.appRoot === 'string' &&
                products.length
                  ? // @ts-ignore
                    `${window.appRoot}/checkout`
                  : null
              }
              data-testid="checkout-link"
              aria-disabled={!products.length}
              disableBtn={!products.length}
              tabIndex={products.length ? 0 : -1}
            >
              Proceed to Checkout
            </S.CheckoutLink>
            {showAltPayments ? (
              <Elements stripe={stripePromise}>
                <StripePayment
                  cartTotal={currentOrderGrandTotalInCents(
                    cartTotal.totalPrice,
                  )}
                  cartNotEmpty={lineItems.length > 0}
                  setTOS={setTOS}
                  isInCart={true}
                  label={ANALYTICS.LABELS.EXPRESS_PAY_CART}
                />
              </Elements>
            ) : null}
            {showTOS ? (
              <S.Agreement>
                <span>By completing your purchase you agree to our </span>
                <a
                  href="//www.everlywell.com/terms-and-conditions/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Terms of Service.
                </a>
              </S.Agreement>
            ) : null}
          </S.Footer>
        ) : null}
      </S.CartContent>
    </>
  );
};

export { Cart };

export default Cart;
